/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

/**
 * `@amzn/prerender-contract` — the browser-safe contract shared between the
 * static-prerender page service and the app it prerenders. Zero dependencies:
 * it pulls in NONE of the server-side render/serve code (Puppeteer, Express),
 * so it's safe in the browser bundle AND safe for `@amzn/static-prerender` to
 * depend on without creating a package cycle back to the app.
 *
 * It carries two things:
 *   - the DOM marker + gql-preseed global constants (see `./contract.js`), used
 *     by both the server (`buildStaticHtml`) and the client hooks below; and
 *   - the boot-time hydration hook a prerendered app calls:
 *       - `clearPrerenderRoot()` — as the live app view mounts, to remove the
 *          prerendered placeholder.
 */

import {
    PRERENDER_PUBLIC_ORIGIN_GLOBAL,
    PRERENDER_ROOT_ATTR,
    STATIC_RENDER_GLOBAL,
} from './contract.ts'

export {
    GQL_PRESEED_GLOBAL,
    PENDING_INSTRUMENTED_GLOBAL,
    PRERENDER_HEAD_ATTR,
    PRERENDER_INVITE_GLOBAL,
    PRERENDER_PUBLIC_ORIGIN_GLOBAL,
    PRERENDER_ROOT_ATTR,
    STATIC_RENDER_GLOBAL,
} from './contract.ts'

/**
 * Remove the prerendered app-root placeholder (the element the server tagged
 * with `data-static-prerender`) so the live app view is the sole content.
 * Call it right as the app mounts its own view. No-op when the page wasn't
 * prerendered.
 *
 * @returns `true` if a placeholder was found and removed.
 */
export function clearPrerenderRoot(): boolean {
    if (typeof document === 'undefined') return false
    const root = document.querySelector(`[${PRERENDER_ROOT_ATTR}]`)
    if (root) {
        root.remove()
        return true
    }
    return false
}

/** True if the current page was served prerendered (a placeholder is present). */
export function isPrerendered(): boolean {
    if (typeof document === 'undefined') return false
    return document.querySelector(`[${PRERENDER_ROOT_ATTR}]`) != null
}

/**
 * True when the app is booting inside a static render (headless browser /
 * jsdom), signalled by `window[STATIC_RENDER_GLOBAL] === true` set by the
 * render path before the app module runs. The app uses this to stub out
 * playback so a render never spins up MSE/EME or creates a `<video>`.
 *
 * Reads the global (not the DOM marker) because it must be true DURING the
 * render itself — before any prerendered placeholder exists — not only when
 * later serving a prerendered page.
 */
export function isStaticRender(): boolean {
    if (typeof window === 'undefined') return false
    return (window as unknown as Record<string, unknown>)[STATIC_RENDER_GLOBAL] === true
}

/** The public origin a static render publishes its SEO URLs on; `undefined` outside a render. */
export function prerenderPublicOrigin(): string | undefined {
    const origin = (globalThis as Record<string, unknown>)[PRERENDER_PUBLIC_ORIGIN_GLOBAL]
    return typeof origin === 'string' ? origin : undefined
}
